import React, { ReactElement } from 'react';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { WithColor } from '@styles/colors';
import TimeDisplay from '@components/common/TimeDisplay';
import TouchableElement from '@components/common/touchableElement';
import { SimpleOnPress } from '@interfaces/base';

type RenderTimeDisplayParams = WithColor & {
  duration: number;
  isSelected: boolean;
  font?: typeof initialFont;
  scale?: ScaleFunction;
};

const renderTimeDisplay = ({ C, duration, font = initialFont, scale = initialScale, isSelected }: RenderTimeDisplayParams) => {
  return (
    <View style={{ flexShrink: 0, marginLeft: scale(5) }}>
      <TimeDisplay
        color={isSelected ? 'Label/Light/AlwaysLight Primary' : 'Label/Light/Tertiary'}
        value={duration}
        C={C}
        fontForDigit="Time 2"
        fontForSecondLetter="Caption 2"
        fontForLetter="Footnote"
        fontForSecondDigit="Time 3"
        includeSecondsAlways={false}
        font={font}
        scale={scale}
      />
    </View>
  );
};

export type RenderJobListItemParams = SimpleOnPress &
  WithColor & {
    title: string;
    duration: number | null;
    isSelected: boolean;
    scale: ScaleFunction;
    font: typeof initialFont;
    bg: string;
    height?: number;
    style?: StyleProp<ViewStyle>;
    fontType?: keyof typeof initialFont;
    ignoreDefaultStyles?: boolean;
    CountComponent?: ReactElement;
    testID?: string;
    selectedTestID?: string;
  };

export const renderJobListItem = ({
  title,
  duration,
  isSelected,
  onPress,
  scale,
  C,
  font,
  bg,
  height = 44,
  fontType = 'Body',
  ignoreDefaultStyles,
  style,
  CountComponent,
  testID,
  selectedTestID,
}: RenderJobListItemParams) => {
  const itemStyles: StyleProp<ViewStyle> = {
    height: scale(height),
    borderRadius: scale(10),
    paddingHorizontal: scale(12),
    justifyContent: 'center',
  };

  return (
    <TouchableElement key={title} testID={testID} onPress={onPress}>
      {({ boxStyle, pressHoverColor }) => (
        <View
          style={[
            boxStyle,
            !ignoreDefaultStyles && itemStyles,
            {
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: isSelected ? C('Selected/Light/List item') : C(pressHoverColor(bg)),
            },
            style,
          ]}
        >
          {isSelected && selectedTestID ? <View testID={selectedTestID} style={{ display: 'none' }} /> : null}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(3), flex: 1 }}>
            {CountComponent && CountComponent}
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={[font[fontType], { color: C(isSelected ? 'Label/Light/AlwaysLight Primary' : 'Label/Light/Primary') }]}
            >
              {title}
            </Text>
          </View>
          {duration !== null && renderTimeDisplay({ duration, isSelected, C, font, scale })}
        </View>
      )}
    </TouchableElement>
  );
};
